import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Container, Tab, Tabs } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const SERVER = process.env.REACT_APP_SERVER;
const serviceFileUrl = (fileName) => `${SERVER}/api/upload/homeFiles/${fileName}`;

const service_hero = serviceFileUrl('service_main_img.svg');
const app_section_img = serviceFileUrl('app_section_img.svg');
const app_section_img2 = serviceFileUrl('app_section_mallangpocket.png');
const app_section_img3 = serviceFileUrl('app_section_mallangbook.png');
const app_section_morning = serviceFileUrl('icon_morning.png');
const app_section_afternoon = serviceFileUrl('icon_afternoon.png');
const app_section_night = serviceFileUrl('icon_night.png');
const app_section_weekend = serviceFileUrl('icon_weekend.png');

const function_pocket_chat = serviceFileUrl('chat.png');
const function_pocket_calendar = serviceFileUrl('calendar.png');
const function_pocket_calendar2 = serviceFileUrl('calendar2.png');
const function_pocket_report = serviceFileUrl('report.png');
const function_pocket_report2 = serviceFileUrl('report2.png');
const function_pocket_report3 = serviceFileUrl('report3.png');
const function_pocket_assessment = serviceFileUrl('assessment.png');
const function_pocket_assessment2 = serviceFileUrl('assessment2.png');

const function_book_weeklyForecast = serviceFileUrl('weekly_forecast.png');
const function_book_observationRecord = serviceFileUrl('observation_record.png');
const function_book_mindWeatherCheck = serviceFileUrl('mind_weather_check.png');
const function_book_weeklyChart = serviceFileUrl('weekly_chart.png');
const function_book_mindClimateSurvey = serviceFileUrl('mind_climate_survey.png');
const function_book_goToPhrase = serviceFileUrl('go_to_phrase.png');
const function_book_startingPoint = serviceFileUrl('starting_point.png');
const function_book_mindWeatherFlow = serviceFileUrl('mind_weather_flow.png');

// Colors based on the provided HTML
// Colors unified with Home/Main atmosphere
const colors = {
    bg: '#ffffff',
    text: '#1a1a1a', // standard text in Home.js
    primary: '#005a8d', // Garam Mind Navy Blue
    primaryDark: '#004870',
    primaryLight: '#f5f9fc',
    accent: '#045584',
    orange500: '#f97316',
    orange400: '#fb923c',
    grayDesc: '#555555', // text.secondary in Home.js
    grayLight: '#f8f9fa',
    stone200: '#e7e5e4',
    navyBg: '#045584', // Same as Home.js footer/mallang section
};

// 어플 설명 섹션 - 말랑포켓 & 말랑북 소개 카드
const introCard = [
    {
        name: '말랑포켓',
        desc: '대화 · 기분 기록 · AI 감정 리포트를 통해 일상 속 작은 감정 변화를 스스로 알아차리고 돌보는 습관을 만들어주는 정서 관리 앱',
        img: app_section_img2
    },
    {
        name: '말랑북',
        desc: '나의 감정, 생각, 행동을 직접 적어보며 나를 더 깊이 이해할 수 있도록 돕는 감정 기록 워크북',
        img: app_section_img3
    }
]

// 어플 설명 섹션 - 말랑포켓 & 말랑북과 함께 하는 하루
const stepByDay = [
    {
        id: '01',
        slot: '아침',
        desc: '기분 캘린더로 오늘 컨디션 체크',
        imgSrc: app_section_morning,
    },
    {
        id: '02',
        slot: '낮',
        desc: '힘든 순간엔 말랑이에게 털어놓기',
        imgSrc: app_section_afternoon,
    },
    {
        id: '03',
        slot: '밤',
        desc: '말랑북 기록으로 하루 마무리',
        imgSrc: app_section_night,
    },
    {
        id: '04',
        slot: '주말',
        desc: '말랑북 주간 마음 예보 & \n 기상도 작성하기',
        imgSrc: app_section_weekend,
    },
];

// 말랑포켓 핵심 기능
const function_pocket = [
    {
        'title': '대화하기',
        'subtitle': '누군가에게 마음을 털어놓고 싶다면?',
        'desc': '대화형 AI ‘말랑이’와 편하게 말해보세요. 대화 내용은 이후 감정 분석과 리포트 생성에도 활용됩니다.',
        'img':[
            { src: function_pocket_chat, alt: '대화하기' },
        ]
    },
    {
        'title': '기분 캘린더',
        'subtitle': '내 마음을 더 자세히',
        'desc': '오늘의 기분을 색과 이모지로 남겨보세요. 매일 쌓인 기록이 정서 변화와 악화 징후를 미리 알려줍니다.',
        'img':[
            { src: function_pocket_calendar, alt: '기분 캘린더' },
            { src: function_pocket_calendar2, alt: '기분 캘린더2' },
        ]
    },
    {
        'title': '감정 리포트',
        'subtitle': '내 마음의 변화를 한눈에',
        'desc': [
            'AI가 대화와 기록을 분석해 주간·월간·종합 리포트로 정리해드립니다. 그래프와 요약으로 내 정서 흐름을 한눈에 확인할 수 있습니다.',
            '※ 유료 요금제 이용 시 감정 상세 분석 및 종합 리포트 전체 페이지를 조회할 수 있습니다.'
        ],
        'img':[
            { src: function_pocket_report, alt: '감정 리포트' },
            { src: function_pocket_report2, alt: '감정 리포트2' },
            { src: function_pocket_report3, alt: '감정 리포트3' }
        ]
    },
    {
        'title': '심리검사',
        'subtitle': '전문 척도로 확인하는 지금 내 마음',
        'desc': [
            '전 세계적으로 널리 쓰이는 PHQ-9(우울), GAD-7(불안) 척도로 지금 내 마음 상태를 점검할 수 있습니다.',
            '※ 심리 검사 결과는 전문적인 진단을 대신하지 않습니다. 결과가 걱정된다면 가까운 전문기관이나 상담사와 상의해보세요.'
        ],
        'img':[
            { src: function_pocket_assessment, alt: '심리검사' },
            { src: function_pocket_assessment2, alt: '심리검사2' },
        ]
    }
]

// 말랑포켓 요금제
const pocket_subscription = [
    {
        name: '베이직',
        fee: '무료',
        period: '',
        features: ['간단한 감정 통계', '종합 리포트 일부 조회'],
    },
    {
        name: '스탠다드',
        fee: '8,800원',
        period: '/ 월',
        features: ['감정 상세 분석', '종합 리포트 전체 조회'],
    },
]

// 말랑북 핵심 기능
const function_book_part1 = [
    {
        'title': '주간 마음 예보',
        'subtitle': '미리 예보하는 이번 주',
        'desc': '이번 주에 기대/걱정되는 일을 적어보세요. 미리 대처 계획도 적어본다면 마음이 편안해집니다.',
        'img':[
            { src: function_book_weeklyForecast, alt: '주간마음예보' },
        ]
    },
    {
        'title': '오늘의 마음 관찰 일지',
        'subtitle': '오늘 하루, 내 마음은 어땠을까?',
        'desc': '오늘의 몸 상태와 에너지, 마음의 날씨를 표시해보세요. 오늘 있었던 일과 마음을 적을 때는 말랑포켓의 말랑이와 대화 후 기억에 남은 한마디를 담아도 좋아요.',
        'img':[
            { src: function_book_observationRecord, alt: '마음관찰일지' },
            { src: function_book_mindWeatherCheck, alt: '마음날씨확인' }
        ]
    },
    {
        'title': '주간 마음 기상도',
        'subtitle': '나를 웃게 한 순간과 힘들게 한 순간',
        'desc': '나를 채운 순간과 힘들게 한 순간을 떠올려 보세요. 새롭게 알게 된 모습도 같이 적으면서 나를 이해하고 한 주를 돌아봅니다.',
        'img':[
            { src: function_book_weeklyChart, alt: '주간마음기상도' },
        ]
    },
]

const function_book_part2 = [
    {
        'title': '마음 기후 조사',
        'subtitle': '나를 가장 많이 찾아온 감정은?',
        'desc': '각 날짜에 감정별 비중을 표시해보세요. 말랑포켓 감정분석보고서를 참고할 수 있습니다.',
        'img':[
            { src: function_book_mindClimateSurvey, alt: '마음기후조사' },
        ]
    },
    {
        'title': '나의 단골 멘트 모음',
        'subtitle': '습관처럼 내게 하는 말',
        'desc': '스스로에게 하는 말을 모아서 더 다정한 말로 바꿔보세요. 바꾸기 어렵다면 말랑포켓의 말랑이와 연습할 수 있습니다.',
        'img':[
            { src: function_book_goToPhrase, alt: '단골멘트모음' },
        ]
    },
    {
        'title': '마음 날씨 패턴 분석',
        'subtitle': '내 마음이 흔들리는 이유',
        'desc': '주차별 내 감정 강도와 반응을 확인하고 네 가지 단서를 생각합니다. 마음이 흔들리는 과정도 4단계로 나누어 분석합니다.',
        'img':[
            { src: function_book_startingPoint, alt: '마음날씨시작점' },
            { src: function_book_mindWeatherFlow, alt: '마음날씨흐름' }
        ]
    },
]

// 핵심 기능 설명 컴포넌트
const CoreFunction = ({data, swiperWidth = '500px'}) => {
    return (
        <Box>
            {data.map((func, index) => (
                <Box
                    key={func.title}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                        justifyContent: 'space-evenly',
                        alignItems: 'center',
                        mb: { xs: 6, md: 8 },
                        gap: { xs: 4, md: 0 },
                    }}
                >
                    <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                        <Box sx={{ width: { xs: '100%', md: '70%' }, mx: 'auto' }}>
                            <Typography 
                                variant="h2" 
                                sx={{ fontSize: '15px', fontWeight: 700, color: colors.primary}}
                            >
                                {func.title}
                            </Typography>
                            <Typography 
                                variant="h3" 
                                sx={{ 
                                    fontSize: { xs: '1.7rem', md: '2.3rem' }, 
                                    fontWeight: 800,
                                    lineHeight: 1.35,
                                    wordBreak: 'keep-all',
                                    whiteSpace: 'pre-line',
                                    mb: 2
                                }}
                            >
                                {func.subtitle}
                            </Typography>
                            {Array.isArray(func.desc) ? (
                                func.desc.map((text, index) => (
                                    <Box key={index}>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontSize: '1.05rem', color: colors.grayDesc, wordBreak: 'keep-all', lineHeight: 1.6 }}
                                        >
                                            {text}
                                        </Typography>
                                        <br />
                                    </Box>
                                ))
                            ) : (
                                <Typography
                                    variant="body1"
                                    sx={{ fontSize: '1.05rem', color: colors.grayDesc, wordBreak: 'keep-all', lineHeight: 1.6 }}
                                >
                                    {func.desc}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', justifyContent: 'center' }}>
                        <Swiper
                            modules={[Pagination]}
                            pagination={{ clickable: true }}
                            style={{ width: swiperWidth, maxWidth: '60vw' }}
                        >
                            {func.img.map((image)=> (
                                <SwiperSlide key={image.alt}>
                                    <Box
                                        sx={{
                                            borderRadius: '24px',
                                            overflow: 'hidden',
                                            pb: '30px'
                                        }}
                                    >
                                        <img
                                            src={image.src}
                                            alt={image.alt}
                                            style={{ width: '100%', borderRadius: '24px', display: 'block' }}
                                        />

                                    </Box>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

CoreFunction.propTypes = {
    data: PropTypes.array.isRequired,
    swiperWidth: PropTypes.string
};

// 말랑북 파트 헤더 컴포넌트
const BookPartHeader = ({ number, title, desc }) => (
    <Box sx={{ display: 'flex', gap: { xs: 3, md: 4 }, mt: { xs: 8, md: 10 }, mb: 6 }}>
        {/* 스텝 인디케이터 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <Box
                sx={{
                    width: { xs: 52, md: 64 },
                    height: { xs: 52, md: 64 },
                    borderRadius: '50%',
                    bgcolor: colors.primary,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 900,
                    fontSize: { xs: '1.3rem', md: '1.6rem' },
                    letterSpacing: '-0.02em',
                    boxShadow: '0 4px 16px rgba(4,85,132,0.25)',
                    flexShrink: 0,
                }}
            >
                {number}
            </Box>


        </Box>

        {/* 텍스트 */}
        <Box sx={{ pt: 1 }}>
            <Typography
                variant="h5"
                sx={{ fontWeight: 800, color: colors.text, letterSpacing: '-0.02em', mb: 1 }}
            >
                {title}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grayDesc, wordBreak: 'keep-all' }}>
                {desc}
            </Typography>
        </Box>
    </Box>
);

BookPartHeader.propTypes = {
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    desc: PropTypes.string.isRequired,
};

function Service() {
    const [functionCategory, setFunctionCategory] = useState('mallang-pocket');

    return (
        <Box sx={{ bgcolor: colors.bg, color: colors.text, minHeight: '100vh' }}>
            {/* Hero Section */}
            <Box
                component="header"
                sx={{
                    height: { xs: '70vh', md: '550px' },
                    minHeight: '400px',
                    width: '100%',
                    // bgcolor: colors.primaryLight,
                    backgroundImage: `url(${service_hero})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'relative',
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            textAlign: 'left',
                            color: '#1a1a1a',
                            maxWidth: { md: '55%' },
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 900,
                                mb: 2,
                                letterSpacing: '0.1em',
                                color: '#1a1a1a',
                                fontSize: { xs: '1rem', md: '1.25rem' },
                            }}
                        >
                            SERVICE
                        </Typography>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 300,
                                mb: 1,
                                fontSize: { xs: '1.7rem', md: '2.9rem' },
                                wordBreak: 'keep-all',
                                color: '#1a1a1a',
                            }}
                        >
                            마음을 돌보는 두 가지 방법
                        </Typography>
                        <Typography
                            variant="h3"
                            component="p"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.5rem', md: '3.2rem' },
                                wordBreak: 'keep-all',
                                whiteSpace: 'nowrap',
                                color: '#1a1a1a',
                            }}
                        >
                            가람마인드의 마음 케어 서비스
                        </Typography>
                        <Typography sx={{ color: colors.grayDesc, fontSize: '1.1rem', mt: 2, lineHeight: 1.7, wordBreak: 'keep-all' }}>
                            일상 속 기록부터 손으로 직접 써 내려가는 시간까지,
                            <br />
                            가람마인드는 디지털과 아날로그를 연결해 마음을 돌아볼 수 있는 도구를 제공합니다.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* 어플 설명 Section */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    {/* 예방 중심 설명 영역 */}
                    <Box mb={2}>
                        <Typography
                            sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.14em', color: '#005a8d', mb: '12px' }}
                        >
                            ABOUT SERVICE
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, mb: 2, letterSpacing: '-0.02em' }}
                        >
                            사후 치료보다
                            <br />
                            예방 중심의 마음관리
                        </Typography>
                        <Typography
                            sx={{
                                color: colors.grayDesc,
                                fontSize: '1.1rem',
                                wordBreak: 'keep-all',
                                lineHeight: 1.6,
                            }}
                        >
                            대부분의 마음 관리는 문제가 이미 커진 뒤에야 시작됩니다.
                        </Typography>
                    </Box>

                    <Box>
                        <img src={app_section_img} alt="사후치료에서 예방중심으로" style={{ width: '100%', maxWidth: '800px', display: 'block', margin: '6px auto' }} />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: 3,
                                mb: 10,
                            }}
                        >
                            {introCard.map((app) => (
                                <Box
                                    key={app.name}
                                    sx={{
                                        flex: 1,
                                        bgcolor: colors.grayLight,
                                        borderRadius: 4,
                                        p: 4,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                        transition: '0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 24px rgba(0,90,141,0.08)',
                                        },
                                    }}
                                >
                                    <img src={app.img} alt={app.name} style={{ width: '128px', height: '155px', flexShrink: 0 }} />
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '1.15rem' }}>
                                            {app.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight:500, color: colors.grayDesc, lineHeight: 1.7, wordBreak: 'keep-all', fontSize:'0.95rem' }}
                                        >
                                            {app.desc}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    
                    {/* quote 영역 */}
                    <Box
                        sx={{
                            py: { xs: 8, md: 12 },
                            mb: 10,
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: '3rem', md: '4rem' },
                                color: colors.primary,
                                lineHeight: 0.5,
                                mb: 2,
                                mt: 2,
                            }}
                        >
                            “
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: '1.3rem', md: '1.7rem' },
                                fontWeight: 800,
                                color: colors.text,
                                wordBreak: 'keep-all',
                                letterSpacing: '-0.02em',
                                mb: 1,
                            }}
                        >
                            오늘, 당신의 마음은 안녕한가요?
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: '1.05rem', md: '1.2rem' },
                                fontWeight: 500,
                                color: colors.grayDesc,
                                wordBreak: 'keep-all',
                            }}
                        >
                            말랑포켓과 말랑북이 그 답을 함께 찾아드릴게요.
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: '3rem', md: '4rem' },
                                color: colors.primary,
                                lineHeight: 0.5,
                                mt: 4,
                                display: 'inline-block',
                                transform: 'rotate(180deg)',
                            }}
                        >
                            “
                        </Typography>
                    </Box>

                    {/* 루틴 영역 */}
                    <Typography
                        sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', color: '#005a8d', mb: '12px' }}
                    >
                        DAILY ROUTINE
                    </Typography>
                    <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, mb: 4, letterSpacing: '-0.02em' }}
                    >
                        말랑포켓&말랑북과 함께 하는 하루
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: 'wrap',
                            gap: 3,
                        }}
                    >
                        {stepByDay.map((step) => (
                            <Box
                                key={step.id}
                                sx={{
                                    flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 0' },
                                    bgcolor: colors.grayLight,
                                    p: 4,
                                    borderRadius: 4,
                                    textAlign: 'center',
                                    transition: '0.3s',
                                    '&:hover': {
                                        bgcolor: colors.primaryLight,
                                        transform: 'translateY(-4px)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        mb: 2,
                                    }}
                                >
                                    <img src={step.imgSrc} alt={step.slot} style={{ width: '28px', height: '28px' }} />
                                    <Typography sx={{ fontWeight: 700, color: colors.primary, fontSize: '1.15rem' }}>
                                        {step.slot}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{ color: colors.grayDesc, wordBreak: 'keep-all', lineHeight: 1.6, whiteSpace: 'pre-line', fontSize: '0.9rem' }}
                                >
                                    {step.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* 핵심 기능 Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 } }}>
                <Typography
                    sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', color: '#005a8d', mb: '12px' }}
                >
                    CORE FEATURES
                </Typography>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, mb: 2, letterSpacing: '-0.02em' }}
                >
                    핵심 기능
                </Typography>
                <Tabs
                    value={functionCategory}
                    onChange={(e, value) => setFunctionCategory(value)}
                    variant="fullWidth"
                    sx={{
                        mb: 4,
                        minHeight: 56,
                        '& .MuiTab-root': {
                            minHeight: 56,
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 2,
                            fontSize: '1rem',
                        },
                        '& .MuiTabs-indicator': { bgcolor: colors.primary },
                    }}
                >
                    <Tab value="mallang-pocket" label="말랑포켓" />
                    <Tab value="mallang-book" label="말랑북" />
                </Tabs>

                {functionCategory === 'mallang-pocket' && (
                    <Box sx={{ mt: 10 }}>
                        {/* 기능 */}
                        <CoreFunction data={function_pocket} swiperWidth="350px" />

                        {/* 요금제 설명 */}
                        <Box sx={{ py: { xs: 6, md: 10 } }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    textAlign: 'center',
                                    mb: 1,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                요금제
                            </Typography>
                         
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 3, md: 4 },
                                    mx: 'auto',
                                    maxWidth: 560,
                                    justifyContent: 'center',
                                    alignItems: { xs: 'center', sm: 'stretch' },
                                }}
                            >
                                {pocket_subscription.map((sub) => (
                                    <Box
                                        key={sub.name}
                                        sx={{
                                            position: 'relative',
                                            flex: 1,
                                            width: { xs: '100%', sm: 'auto' },
                                            maxWidth: { xs: 320, sm: 'none' },
                                            px: 4,
                                            py: 5,
                                            borderRadius: '24px',
                                            border: '1.5px solid #e0e0e0',
                                            bgcolor: 'transparent',
                                            color: '#222',
                                            transition: 'transform 0.25s, box-shadow 0.25s',
                                            '&:hover': {
                                                transform: 'translateY(-6px)',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                                            },
                                        }}
                                    >
                                        
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                                mb: 2,
                                                color: '#666',
                                                letterSpacing: '0.05em',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {sub.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, mb: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: '2.2rem',
                                                    lineHeight: 1,
                                                    color: 'inherit',
                                                    letterSpacing: '-0.03em',
                                                }}
                                            >
                                                {sub.fee}
                                            </Typography>
                                            {sub.period && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.9rem',
                                                        color: '#888',
                                                        mb: 0.4,
                                                    }}
                                                >
                                                    {sub.period}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                my: 3,
                                                borderTop: '1px solid #eee',
                                            }}
                                        />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {sub.features.map((f, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            bgcolor: '#045584',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.9rem',
                                                            color: '#444',
                                                        }}
                                                    >
                                                        {f}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}

                {functionCategory === 'mallang-book' && (
                    <Box>
                        {/* PART 1 */}
                        <BookPartHeader number={1} title='마음 날씨 관측하기' desc='일정 기간 동안 매일의 몸 상태와 감정을 기록하는 파트입니다.' />
                        <CoreFunction data={function_book_part1} />

                        {/* PART 2 */}
                        <BookPartHeader number={2} title='내 마음 속 비밀 연구소' desc='기록을 마친 뒤, 쌓인 데이터를 바탕으로 나의 마음 패턴을 분석하고 정리합니다.' />
                        <CoreFunction data={function_book_part2} />
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default Service;
